import { useState } from 'react';

import type { PlaceholderItem } from '../types';

/**
 * テンプレート凡例の中で「クリックでクリップボードコピーできる」プレースホルダ表示。
 *
 * - `<code>` 風の見た目を維持しつつ `<button>` 要素として描画する。
 * - クリックすると `navigator.clipboard.writeText` でトークン文字列をコピーし、
 *   一時的に「コピーしました」を吹き出しで表示する。
 * - クリップボード API が使えない環境 (古いブラウザ / セキュア コンテキスト外) では
 *   そっとフォールバックして何もしない (テキスト選択は可能)。
 *
 * ユーザは説明文を「読む」というより、SSML 入力欄に `{delay_minutes_1}` 等を
 * コピペして書き始める用途で凡例を使うため、クリック 1 回でコピーできる UI を採用する。
 */
function CopyableToken({ token }: { token: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(token).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      });
    }
  };
  return (
    <button
      type="button"
      className="copyable-token"
      onClick={handleCopy}
      title={`${token} をコピー`}
      aria-label={`${token} をクリップボードにコピー`}
      style={{
        font: 'inherit',
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
        background: '#f4f6f8',
        border: '1px solid #d0d7de',
        borderRadius: 4,
        padding: '1px 6px',
        cursor: 'pointer',
        color: '#24292f',
      }}
    >
      <code style={{ background: 'transparent', padding: 0 }}>{token}</code>
      {copied ? (
        <span
          aria-live="polite"
          style={{
            marginLeft: 4,
            fontSize: '0.85em',
            color: '#0969da',
          }}
        >
          ✓ コピー済み
        </span>
      ) : null}
    </button>
  );
}

/**
 * プレースホルダ凡例の共通 UI。
 * トークンと簡易説明をリストで並べ、各トークンをクリックでクリップボードコピーできる。
 *
 * 現場 WG 要望 (受け入れ条件「入力補助用のコピーできるやつは折りたたまれており、
 * 利用したい時だけ展開できること」) に対応して `<details>` で折りたたむ。
 * - デフォルトは閉じた状態 (`open` 属性なし) で、見出し (`<summary>`) をクリックで展開。
 * - `data-testid` / `.placeholder-legend` クラスは従来どおり最外要素に付与し、
 *   既存の E2E / ユニットテストからの参照互換を保つ。
 * - `<details>` は閉じていても中身 (トークン一覧) が DOM 上に存在するため、
 *   `hint.textContent` でプレースホルダ文言をアサートする既存テストも動作する。
 */
export function PlaceholderLegend({
  items,
  testId,
  note,
}: {
  items: PlaceholderItem[];
  testId?: string;
  /** 凡例の下に追加で表示する補足文 (任意) */
  note?: React.ReactNode;
}): React.JSX.Element {
  return (
    <details className="placeholder-legend hint" data-testid={testId} style={{ margin: '4px 0' }}>
      <summary className="placeholder-legend__summary">
        利用できるプレースホルダ (クリックでコピー)
      </summary>
      <div className="placeholder-legend__body">
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 8px',
            alignItems: 'baseline',
          }}
        >
          {items.map((item) => (
            <li key={item.token} style={{ display: 'contents' }}>
              <CopyableToken token={item.token} />
              <span>{item.description}</span>
            </li>
          ))}
        </ul>
        {note ? <div style={{ marginTop: 6 }}>{note}</div> : null}
      </div>
    </details>
  );
}
